import React,{useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PostArtwork from './PostArtwork';
import * as api from '../lib/api.js';
import {
  getLayoutOverride,
  hasCustomLayoutOverride
} from '../data/layoutOverrides';
import {getPostLayoutVocabulary} from '../data/postLayoutVocabulary';
import './BrandSetup.css';

const {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiPlus,
  FiSettings,
  FiTrash2,
  FiZap
}=FiIcons;

const displayFonts=[
  'DM Serif Display',
  'Playfair Display',
  'Georgia',
  'DM Sans',
  'Bebas Neue',
  'Oswald'
];
const bodyFonts=[
  'DM Sans',
  'Arial',
  'Helvetica',
  'Georgia',
  'Source Sans 3',
  'IBM Plex Sans'
];
const layoutOptions=[
  {id:'editorial',name:'Original Editorial'},
  {id:'bold',name:'Bold Statement'},
  {id:'quote',name:'Quote / Stat Card'},
  {id:'bottom',name:'Bottom-Anchored'},
  {id:'split',name:'Split Panel'},
  {id:'fullbleed',name:'Full Bleed Type'},
  {id:'framed',name:'Framed'}
];
const writingPairs=[
  {
    id:'clarity',
    punchy:'Clarity changes everything.',
    warm:'A little more clarity can change the way forward.'
  },
  {
    id:'growth',
    punchy:'Build for what comes next.',
    warm:'Create a foundation that can grow with your ambitions.'
  },
  {
    id:'expertise',
    punchy:'Good strategy creates options.',
    warm:'Thoughtful strategy gives you more options when plans change.'
  }
];

const basicsPreviewLayouts=['editorial','bold','quote'];
const basicsPreviewImage=
  'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=85';
const layoutPreviewPost={
  headline:'Your headline goes here',
  supporting:'Supporting copy appears here.',
  image:basicsPreviewImage
};

const ctaTypeOptions=[
  {value:'booking',label:'Booking',hint:'Schedule a call or meeting'},
  {value:'info',label:'Info',hint:'Learn more / educational'},
  {value:'event',label:'Event',hint:'Workshop or event invite'},
  {value:'engagement',label:'Engagement',hint:'Comment, save, or share prompts'},
  {value:'contact',label:'Contact',hint:'DM or message us'}
];

const BUSINESS_FIELD_LIMITS={
  businessSummary:500,
  productsServices:800,
  mission:800,
  vision:800,
  targetAudience:800,
  strategicValue:800
};

const EXPANDABLE_BUSINESS_FIELDS=[
  'productsServices',
  'mission',
  'vision',
  'targetAudience',
  'strategicValue'
];

function getMonogram(name) {
  const words=name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'NB';
  return words.slice(0,2).map((word)=> word[0]).join('').toUpperCase();
}

function getInitialForm(brand) {
  const writingStyle=brand?.writingStyle || 'Punchy & direct';
  const choice=writingStyle==='Warm & descriptive' ? 'warm' : 'punchy';

  return {
    name:brand?.name || '',
    logo:brand?.logo || '',
    primary:brand?.primary || '#19382f',
    accent:brand?.accent || '#c99b5a',
    displayFont:brand?.displayFont || 'DM Serif Display',
    bodyFont:brand?.bodyFont || 'DM Sans',
    tone:brand?.tone || {
      formality:3,
      energy:3,
      warmth:3,
      notes:brand?.voice || ''
    },
    ctaOptions:brand?.ctaOptions?.length ? [...brand.ctaOptions] : [],
    writingChoices:Object.fromEntries(
      writingPairs.map((pair)=> [pair.id,choice])
    ),
    preferredLayouts:brand?.preferredLayouts?.length
      ? brand.preferredLayouts
      : ['editorial','bold','bottom'],
    layoutOverrides:brand?.layoutOverrides
      ? {...brand.layoutOverrides}
      : {},
    businessSummary:brand?.businessSummary || '',
    productsServices:brand?.productsServices || '',
    mission:brand?.mission || '',
    vision:brand?.vision || '',
    targetAudience:brand?.targetAudience || '',
    strategicValue:brand?.strategicValue || ''
  };
}

function BrandSetup({brand,mode,loading,onSave,onCancel}) {
  const [step,setStep]=useState(0);
  const [form,setForm]=useState(()=> getInitialForm(brand));
  const [logoFile,setLogoFile]=useState(null);
  const [saving,setSaving]=useState(false);
  const [saveError,setSaveError]=useState(null);
  const monogram=useMemo(()=> getMonogram(form.name),[form.name]);
  const writingStyle=useMemo(()=> {
    const punchyCount=Object.values(form.writingChoices)
      .filter((choice)=> choice==='punchy').length;
    return punchyCount>=2 ? 'Punchy & direct' : 'Warm & descriptive';
  },[form.writingChoices]);

  useEffect(()=> {
    setForm(getInitialForm(brand));
    setLogoFile(null);
    setSaveError(null);
    setStep(0);
  },[brand,mode]);

  const update=(field,value)=> {
    setForm((current)=> ({...current,[field]:value}));
  };

  const updateTone=(field,value)=> {
    setForm((current)=> ({
      ...current,
      tone:{...current.tone,[field]:value}
    }));
  };

  const updateCtaOptions=(ctaOptions)=> {
    setForm((current)=> ({...current,ctaOptions}));
  };

  const applyVoiceDraft=(draft)=> {
    setForm((current)=> ({
      ...current,
      tone:{
        ...current.tone,
        formality:draft.formality,
        energy:draft.energy,
        warmth:draft.warmth,
        notes:draft.notes
      },
      ctaOptions:draft.ctaOptions
    }));
  };

  const uploadLogo=(event)=> {
    const file=event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setLogoFile(file);
    update('logo',URL.createObjectURL(file));
  };

  const save=async ()=> {
    const notes=form.tone.notes.trim();
    setSaving(true);
    setSaveError(null);

    const result=await onSave({
      name:form.name.trim(),
      logo:form.logo,
      logoFile,
      monogram,
      primary:form.primary,
      accent:form.accent,
      displayFont:form.displayFont,
      bodyFont:form.bodyFont,
      tone:{...form.tone,notes},
      ctaOptions:form.ctaOptions.filter((cta)=> cta.label.trim()),
      writingStyle,
      preferredLayouts:form.preferredLayouts,
      layoutOverrides:form.layoutOverrides,
      businessSummary:form.businessSummary,
      productsServices:form.productsServices,
      mission:form.mission,
      vision:form.vision,
      targetAudience:form.targetAudience,
      strategicValue:form.strategicValue,
      voice:notes || `${writingStyle}, with a considered and consistent tone.`
    });

    if (result?.error) {
      setSaveError(result.error);
    }

    setSaving(false);
  };

  const steps=[
    <BasicsStep
      form={form}
      monogram={monogram}
      onUpdate={update}
      onUpload={uploadLogo}
    />,
    <AboutBusinessStep
      form={form}
      onUpdate={update}
    />,
    <ToneStep
      form={form}
      brandId={brand?.id}
      onUpdate={updateTone}
      onUpdateCtaOptions={updateCtaOptions}
      onApplyVoiceDraft={applyVoiceDraft}
    />,
    <WritingStep form={form} onUpdate={update} style={writingStyle} />,
    <LayoutStep form={form} monogram={monogram} onUpdate={update} />,
    <ReviewStep
      form={form}
      monogram={monogram}
      writingStyle={writingStyle}
      brandId={brand?.id}
      savedPreferredLayouts={brand?.preferredLayouts}
      initialLayoutVocabulary={brand?.layoutVocabulary}
      onEdit={setStep}
    />
  ];

  const canContinue=step!==0 || Boolean(form.name.trim());
  const canSave=Boolean(form.name.trim() && form.preferredLayouts.length);

  if (loading) {
    return (
      <motion.main
        className="page brand-setup-page"
        initial={{opacity:0}}
        animate={{opacity:1}}
      >
        <p className="brand-setup-loading">Loading brand profile…</p>
      </motion.main>
    );
  }

  return (
    <motion.main
      className="page brand-setup-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <header className="brand-setup-header">
        <div>
          <span className="eyebrow">
            {mode==='add' ? 'New workspace' : 'Brand settings'}
          </span>
          <h1>
            {mode==='add' ? 'Build your brand profile.' : `Shape ${brand.name}.`}
          </h1>
          <p>
            Define how this brand looks, sounds, and communicates across every
            post.
          </p>
        </div>
        <div className="brand-setup-mark">
          <SafeIcon icon={FiSettings} />
        </div>
      </header>

      <section className={`brand-flow ${step===0 ? 'brand-flow-basics' : ''}`}>
        <Progress step={step} onSelect={setStep} />
        <motion.div
          className="brand-step-card"
          key={step}
          initial={{opacity:0,y:10}}
          animate={{opacity:1,y:0}}
        >
          {steps[step]}
        </motion.div>

        <div className="brand-flow-actions">
          <button type="button" className="text-cancel" onClick={onCancel}>
            Cancel
          </button>
          <div>
            {saveError && (
              <p className="brand-setup-error" role="alert">{saveError}</p>
            )}
            {step>0 && (
              <button
                type="button"
                className="secondary-button"
                onClick={()=> setStep(step-1)}
              >
                <SafeIcon icon={FiChevronLeft} />
                Back
              </button>
            )}
            {step<5 ? (
              <button
                type="button"
                className="primary-button"
                disabled={!canContinue}
                style={{background:form.primary}}
                onClick={()=> setStep(step+1)}
              >
                Next
                <SafeIcon icon={FiChevronRight} />
              </button>
            ) : (
              <button
                type="button"
                className="primary-button"
                disabled={!canSave || saving}
                style={{background:form.primary}}
                onClick={save}
              >
                <SafeIcon icon={FiCheck} />
                {saving ? 'Saving…' : 'Save brand'}
              </button>
            )}
          </div>
        </div>
      </section>
    </motion.main>
  );
}

function Progress({step,onSelect}) {
  const labels=[
    'Basics',
    'About your business',
    'Voice & tone',
    'Writing',
    'Layouts',
    'Review'
  ];

  return (
    <div className="brand-progress">
      <span>Step {step+1} of {labels.length}</span>
      <div className="progress-track">
        {labels.map((label,index)=> (
          <button
            type="button"
            key={label}
            className={index===step ? 'active' : index<step ? 'complete' : ''}
            onClick={()=> onSelect(index)}
          >
            <i>{index<step ? <SafeIcon icon={FiCheck} /> : index+1}</i>
            <small>{label}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepHeading({eyebrow,title,text}) {
  return (
    <div className="brand-step-heading">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

function BasicsStep({form,monogram,onUpdate,onUpload}) {
  return (
    <div className="basics-step">
      <StepHeading
        eyebrow="Step one"
        title="Start with the essentials."
        text="Set the visual foundation people will recognize."
      />
      <div className="basics-step-layout">
        <div className="brand-fields">
          <label className="brand-field">
            Brand name
            <input
              value={form.name}
              onChange={(event)=> onUpdate('name',event.target.value)}
              placeholder="e.g. Foundry Advisory"
              autoFocus
            />
          </label>
          <div className="logo-upload">
            <span className="logo-preview" style={{background:form.primary}}>
              {form.logo
                ? <img src={form.logo} alt="Brand logo preview" />
                : monogram}
            </span>
            <div className="logo-upload-copy">
              <label className="logo-upload-button">
                <SafeIcon icon={FiImage} />
                Upload logo
                <input type="file" accept="image/*" onChange={onUpload} />
              </label>
              <small>Initials are used when no image is selected.</small>
            </div>
          </div>
          <div className="brand-fields two-column">
            <ColorField
              label="Primary color"
              value={form.primary}
              onChange={(value)=> onUpdate('primary',value)}
            />
            <ColorField
              label="Accent color"
              value={form.accent}
              onChange={(value)=> onUpdate('accent',value)}
            />
            <FontField
              label="Display font"
              value={form.displayFont}
              options={displayFonts}
              onChange={(value)=> onUpdate('displayFont',value)}
            />
            <FontField
              label="Body font"
              value={form.bodyFont}
              options={bodyFonts}
              onChange={(value)=> onUpdate('bodyFont',value)}
            />
          </div>
        </div>
        <BasicsPreviewPanel form={form} monogram={monogram} />
      </div>
    </div>
  );
}

function BasicsPreviewPanel({form,monogram}) {
  const brandName=form.name.trim() || 'Your brand name';
  const previewBrand={
    name:brandName,
    monogram,
    primary:form.primary,
    accent:form.accent,
    displayFont:form.displayFont,
    bodyFont:form.bodyFont,
    logo:form.logo,
    layoutOverrides:form.layoutOverrides
  };

  return (
    <aside className="basics-preview" aria-label="Live brand preview">
      <span className="basics-preview-label">Live preview</span>

      <div className="basics-preview-typography">
        <p
          className="basics-preview-name"
          style={{
            fontFamily:`'${form.displayFont}',serif`,
            color:form.primary
          }}
        >
          {brandName}
        </p>
        <p
          className="basics-preview-body"
          style={{fontFamily:`'${form.bodyFont}',sans-serif`}}
        >
          The quick brown fox jumps over the lazy dog.
        </p>
      </div>

      <div className="basics-preview-colors">
        <button
          type="button"
          className="basics-preview-button"
          style={{background:form.primary}}
          tabIndex={-1}
        >
          Primary action
        </button>
        <span
          className="basics-preview-badge"
          style={{background:form.accent}}
        >
          Accent tag
        </span>
      </div>

      <div className="basics-preview-mockups">
        <span className="basics-preview-mockups-label">Post previews</span>
        <div className="basics-preview-mockups-grid">
          {basicsPreviewLayouts.map((layout)=> (
            <div className="basics-preview-mockup" key={layout}>
              <PostArtwork
                compact
                brand={previewBrand}
                post={{
                  headline:'Your headline goes here',
                  supporting:'Supporting copy appears here.',
                  layout,
                  image:basicsPreviewImage
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

function AboutBusinessStep({form,onUpdate}) {
  const [assistLoading,setAssistLoading]=useState(false);
  const [assistError,setAssistError]=useState(null);

  const runAssist=async ()=> {
    const summary=form.businessSummary.trim();

    if (!summary) {
      setAssistError('Add a business summary first — the AI uses it as a starting point.');
      return;
    }

    const hasExistingContent=EXPANDABLE_BUSINESS_FIELDS.some(
      (field)=> form[field]?.trim()
    );

    if (
      hasExistingContent &&
      !window.confirm(
        'This will replace content in the fields below with new AI suggestions. Continue?'
      )
    ) {
      return;
    }

    setAssistLoading(true);
    setAssistError(null);

    const {data,error}=await api.brands.expandBusinessDescription({
      businessSummary:summary
    });

    setAssistLoading(false);

    if (error) {
      setAssistError(error.message || 'AI assist failed. Please try again.');
      return;
    }

    onUpdate('productsServices',data.productsServices || '');
    onUpdate('mission',data.mission || '');
    onUpdate('vision',data.vision || '');
    onUpdate('targetAudience',data.targetAudience || '');
    onUpdate('strategicValue',data.strategicValue || '');
  };

  return (
    <div className="business-context-step">
      <StepHeading
        eyebrow="Step two"
        title="Tell us about your business."
        text="Share what you do and who you serve — this shapes how posts are written for you."
      />

      <div className="business-context-fields">
        <div className="business-assist-row">
          <label className="brand-field tone-notes business-summary-field">
            Business summary
            <textarea
              value={form.businessSummary}
              onChange={(event)=> {
                setAssistError(null);
                onUpdate('businessSummary',event.target.value);
              }}
              placeholder="A short description of what your business does, who it serves, and what makes it distinct."
              maxLength={BUSINESS_FIELD_LIMITS.businessSummary}
            />
            <small>
              {form.businessSummary.length}/{BUSINESS_FIELD_LIMITS.businessSummary} · Optional
            </small>
          </label>
          <div className="business-assist-action">
            <button
              type="button"
              className="secondary-button business-assist-button"
              disabled={assistLoading}
              onClick={runAssist}
            >
              <SafeIcon
                icon={FiZap}
                className={assistLoading ? 'regenerate-spinner' : ''}
              />
              {assistLoading ? 'Writing…' : 'Help me write this'}
            </button>
            <small>
              Expands your summary into the fields below. Edit anything before saving.
            </small>
          </div>
        </div>

        {assistError && (
          <p className="business-assist-error" role="alert">{assistError}</p>
        )}

        <BusinessContextField
          label="Products / services"
          field="productsServices"
          value={form.productsServices}
          placeholder="What you offer — products, services, programs, or packages."
          maxLength={BUSINESS_FIELD_LIMITS.productsServices}
          onUpdate={onUpdate}
        />
        <BusinessContextField
          label="Mission"
          field="mission"
          value={form.mission}
          placeholder="Why your business exists and the purpose it serves."
          maxLength={BUSINESS_FIELD_LIMITS.mission}
          onUpdate={onUpdate}
        />
        <BusinessContextField
          label="Vision"
          field="vision"
          value={form.vision}
          placeholder="Where you're headed — the future you're working toward."
          maxLength={BUSINESS_FIELD_LIMITS.vision}
          onUpdate={onUpdate}
        />
        <BusinessContextField
          label="Target audience"
          field="targetAudience"
          value={form.targetAudience}
          placeholder="Who you serve — clients, customers, or communities you focus on."
          maxLength={BUSINESS_FIELD_LIMITS.targetAudience}
          onUpdate={onUpdate}
        />
        <BusinessContextField
          label="Strategic value"
          field="strategicValue"
          value={form.strategicValue}
          placeholder="What sets you apart — your edge, approach, or promise."
          maxLength={BUSINESS_FIELD_LIMITS.strategicValue}
          onUpdate={onUpdate}
        />
      </div>
    </div>
  );
}

function BusinessContextField({label,field,value,placeholder,maxLength,onUpdate}) {
  return (
    <label className="brand-field tone-notes">
      {label}
      <textarea
        value={value}
        onChange={(event)=> onUpdate(field,event.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
      <small>{value.length}/{maxLength} · Optional</small>
    </label>
  );
}

function mapAiScoreToSlider(value) {
  const clamped=Math.min(100,Math.max(0,Math.round(value)));
  return Math.round((clamped / 100) * 4) + 1;
}

function ToneStep({form,brandId,onUpdate,onUpdateCtaOptions,onApplyVoiceDraft}) {
  const [draftLoading,setDraftLoading]=useState(false);
  const [draftError,setDraftError]=useState(null);
  const ctas=form.ctaOptions;
  const canDraft=Boolean(brandId);

  const addCta=()=> {
    onUpdateCtaOptions([...ctas,{label:'',type:'booking'}]);
  };

  const updateCta=(index,field,value)=> {
    onUpdateCtaOptions(
      ctas.map((cta,i)=> (i===index ? {...cta,[field]:value} : cta))
    );
  };

  const removeCta=(index)=> {
    onUpdateCtaOptions(ctas.filter((_,i)=> i!==index));
  };

  const hasExistingVoiceContent=()=> {
    const slidersChanged=
      form.tone.formality!==3 ||
      form.tone.energy!==3 ||
      form.tone.warmth!==3;
    const hasNotes=Boolean(form.tone.notes.trim());
    const hasCtas=ctas.some((cta)=> cta.label.trim()) || ctas.length>0;

    return slidersChanged || hasNotes || hasCtas;
  };

  const runDraft=async ()=> {
    if (!brandId) return;

    if (
      hasExistingVoiceContent() &&
      !window.confirm(
        'This will replace your current voice settings and CTAs with new AI suggestions. Continue?'
      )
    ) {
      return;
    }

    setDraftLoading(true);
    setDraftError(null);

    const {data,error}=await api.brands.draftVoiceAndCtas(brandId);

    setDraftLoading(false);

    if (error) {
      setDraftError(error.message || 'AI draft failed. Please try again.');
      return;
    }

    onApplyVoiceDraft({
      formality:mapAiScoreToSlider(data.formality),
      energy:mapAiScoreToSlider(data.energy),
      warmth:mapAiScoreToSlider(data.warmth),
      notes:data.voiceNotes || '',
      ctaOptions:(data.suggestedCtas || []).map((cta)=> ({
        label:cta.label,
        type:cta.type
      }))
    });
  };

  return (
    <div>
      <StepHeading
        eyebrow="Step three"
        title="How should the brand sound?"
        text="Set a few simple signals for the voice behind every post."
      />

      <div className="tone-assist-row">
        <button
          type="button"
          className="secondary-button tone-assist-button"
          disabled={!canDraft || draftLoading}
          onClick={runDraft}
        >
          <SafeIcon
            icon={FiZap}
            className={draftLoading ? 'regenerate-spinner' : ''}
          />
          {draftLoading ? 'Drafting…' : 'Draft with AI'}
        </button>
        <small>
          {canDraft
            ? 'Optional starting point — uses your saved business context to suggest voice settings and CTAs. Edit anything afterward.'
            : 'Save your brand\u2019s business info first — AI drafts use the business context stored after your first save.'}
        </small>
      </div>

      {draftError && (
        <p className="tone-assist-error" role="alert">{draftError}</p>
      )}

      <div className="tone-controls">
        <ToneSlider
          label="Formality"
          start="Casual"
          end="Formal"
          value={form.tone.formality}
          onChange={(value)=> onUpdate('formality',value)}
        />
        <ToneSlider
          label="Energy"
          start="Calm"
          end="Energetic"
          value={form.tone.energy}
          onChange={(value)=> onUpdate('energy',value)}
        />
        <ToneSlider
          label="Warmth"
          start="Direct"
          end="Warm"
          value={form.tone.warmth}
          onChange={(value)=> onUpdate('warmth',value)}
        />
        <label className="brand-field tone-notes">
          Anything specific about how this brand talks?
          <textarea
            value={form.tone.notes}
            onChange={(event)=> onUpdate('notes',event.target.value)}
            placeholder="Clear, confident, and never overly sales-focused."
            maxLength={180}
          />
          <small>{form.tone.notes.length}/180 · Optional</small>
        </label>

        <div className="cta-options">
          <div className="cta-options-header">
            <div>
              <strong>Call-to-action options</strong>
              <p>
                Add phrases the brand uses to invite action. These help shape
                captions that match your voice.
              </p>
            </div>
            <button type="button" className="secondary-button cta-add" onClick={addCta}>
              <SafeIcon icon={FiPlus} />
              Add CTA
            </button>
          </div>

          {ctas.length===0 ? (
            <div className="cta-options-empty">
              <p>No CTAs yet.</p>
              <small>
                Optional — add 3–5 phrases like &ldquo;Book a Meeting&rdquo; or
                &ldquo;Learn More&rdquo; so generated captions can draw from
                brand-specific prompts.
              </small>
            </div>
          ) : (
            <ul className="cta-options-list">
              {ctas.map((cta,index)=> (
                <li className="cta-option-row" key={index}>
                  <label className="brand-field">
                    Label
                    <input
                      value={cta.label}
                      onChange={(event)=> updateCta(index,'label',event.target.value)}
                      placeholder='e.g. "Book a Meeting"'
                    />
                  </label>
                  <label className="brand-field">
                    Type
                    <select
                      value={cta.type}
                      onChange={(event)=> updateCta(index,'type',event.target.value)}
                    >
                      {ctaTypeOptions.map((option)=> (
                        <option key={option.value} value={option.value}>
                          {option.label} — {option.hint}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="cta-remove"
                    aria-label="Remove CTA"
                    onClick={()=> removeCta(index)}
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ToneSlider({label,start,end,value,onChange}) {
  return (
    <label className="tone-slider">
      <span>{label}</span>
      <div>
        <small>{start}</small>
        <input
          type="range"
          min="1"
          max="5"
          value={value}
          onChange={(event)=> onChange(Number(event.target.value))}
        />
        <small>{end}</small>
      </div>
    </label>
  );
}

function WritingStep({form,onUpdate,style}) {
  const choose=(id,value)=> {
    onUpdate('writingChoices',{...form.writingChoices,[id]:value});
  };

  return (
    <div>
      <StepHeading
        eyebrow="Step four"
        title="Which phrasing feels more like you?"
        text="Choose one headline from each pair. There are no wrong answers."
      />
      <div className="writing-pairs">
        {writingPairs.map((pair,index)=> (
          <div className="writing-pair" key={pair.id}>
            <span>Example {index+1}</span>
            <div>
              <ChoiceButton
                selected={form.writingChoices[pair.id]==='punchy'}
                onClick={()=> choose(pair.id,'punchy')}
                text={pair.punchy}
              />
              <ChoiceButton
                selected={form.writingChoices[pair.id]==='warm'}
                onClick={()=> choose(pair.id,'warm')}
                text={pair.warm}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="derived-style">
        Current style
        <strong>{style}</strong>
      </p>
    </div>
  );
}

function ChoiceButton({selected,onClick,text}) {
  return (
    <button
      type="button"
      className={selected ? 'selected' : ''}
      onClick={onClick}
    >
      <i>{selected && <SafeIcon icon={FiCheck} />}</i>
      <span>{text}</span>
    </button>
  );
}

function LayoutStep({form,monogram,onUpdate}) {
  const selected=form.preferredLayouts;
  const previewBrand={
    name:form.name.trim() || 'Your brand',
    monogram,
    primary:form.primary,
    accent:form.accent,
    displayFont:form.displayFont,
    bodyFont:form.bodyFont,
    logo:form.logo,
    layoutOverrides:form.layoutOverrides
  };

  const toggle=(id)=> {
    if (selected.includes(id)) {
      if (selected.length===1) return;
      onUpdate('preferredLayouts',selected.filter((item)=> item!==id));
      return;
    }
    onUpdate('preferredLayouts',[...selected,id]);
  };
  const makeDefault=(id)=> {
    onUpdate('preferredLayouts',[id,...selected.filter((item)=> item!==id)]);
  };
  const updateOverride=(layoutId,field,value)=> {
    onUpdate('layoutOverrides',{
      ...form.layoutOverrides,
      [layoutId]:{
        ...getLayoutOverride(form.layoutOverrides,layoutId),
        [field]:value
      }
    });
  };
  const resetOverride=(layoutId)=> {
    const next={...form.layoutOverrides};
    delete next[layoutId];
    onUpdate('layoutOverrides',next);
  };

  return (
    <div>
      <StepHeading
        eyebrow="Step five"
        title="Choose your visual rhythm."
        text="Select layouts that feel on-brand. The first is your preferred default. Fine-tune logo, image, and type sizing per layout below."
      />
      <div className="layout-options">
        {layoutOptions.map((layout)=> {
          const isSelected=selected.includes(layout.id);
          const isDefault=selected[0]===layout.id;

          return (
            <article
              className={`layout-option ${isSelected ? 'selected' : ''}`}
              key={layout.id}
            >
              <button type="button" onClick={()=> toggle(layout.id)}>
                <LayoutThumbnail layout={layout.id} />
                <span>
                  <strong>{layout.name}</strong>
                  <small>{isSelected ? 'Included' : 'Not selected'}</small>
                </span>
                <i>{isSelected && <SafeIcon icon={FiCheck} />}</i>
              </button>
              {isSelected && (
                <button
                  type="button"
                  className={`default-layout ${isDefault ? 'active' : ''}`}
                  onClick={()=> makeDefault(layout.id)}
                >
                  {isDefault ? 'Preferred default' : 'Make default'}
                </button>
              )}
            </article>
          );
        })}
      </div>

      {selected.length>0 && (
        <div className="layout-override-panels">
          <h3 className="layout-override-heading">Layout adjustments</h3>
          {selected.map((layoutId)=> {
            const layoutMeta=layoutOptions.find((layout)=> layout.id===layoutId);
            const override=getLayoutOverride(form.layoutOverrides,layoutId);
            const isCustom=hasCustomLayoutOverride(form.layoutOverrides,layoutId);

            return (
              <section className="layout-override-panel" key={layoutId}>
                <header className="layout-override-panel-header">
                  <div>
                    <strong>{layoutMeta?.name || layoutId}</strong>
                    <small>Live preview updates as you adjust.</small>
                  </div>
                  <button
                    type="button"
                    className="layout-override-reset"
                    onClick={()=> resetOverride(layoutId)}
                    disabled={!isCustom}
                  >
                    Reset to default
                  </button>
                </header>
                <div className="layout-override-body">
                  <div className="layout-override-preview">
                    <PostArtwork
                      compact
                      brand={previewBrand}
                      post={{...layoutPreviewPost,layout:layoutId}}
                    />
                  </div>
                  <div className="layout-override-controls">
                    <OverrideSlider
                      label="Logo scale"
                      min={0.5}
                      max={2}
                      step={0.05}
                      value={override.logoScale}
                      onChange={(value)=> updateOverride(layoutId,'logoScale',value)}
                    />
                    <OverrideSlider
                      label="Logo position X"
                      min={-8}
                      max={8}
                      step={0.5}
                      value={override.logoOffsetX}
                      onChange={(value)=> updateOverride(layoutId,'logoOffsetX',value)}
                    />
                    <OverrideSlider
                      label="Logo position Y"
                      min={-8}
                      max={8}
                      step={0.5}
                      value={override.logoOffsetY}
                      onChange={(value)=> updateOverride(layoutId,'logoOffsetY',value)}
                    />
                    {layoutId!=='fullbleed' && (
                      <>
                        <OverrideSlider
                          label="Image scale"
                          min={0.5}
                          max={2}
                          step={0.05}
                          value={override.imageScale}
                          onChange={(value)=> updateOverride(layoutId,'imageScale',value)}
                        />
                        <OverrideSlider
                          label="Image position X"
                          min={-8}
                          max={8}
                          step={0.5}
                          value={override.imageOffsetX}
                          onChange={(value)=> updateOverride(layoutId,'imageOffsetX',value)}
                        />
                        <OverrideSlider
                          label="Image position Y"
                          min={-8}
                          max={8}
                          step={0.5}
                          value={override.imageOffsetY}
                          onChange={(value)=> updateOverride(layoutId,'imageOffsetY',value)}
                        />
                      </>
                    )}
                    <OverrideSlider
                      label="Headline font scale"
                      min={0.5}
                      max={2}
                      step={0.05}
                      value={override.headlineFontScale}
                      onChange={(value)=> updateOverride(
                        layoutId,
                        'headlineFontScale',
                        value
                      )}
                    />
                    <OverrideSlider
                      label="Subhead font scale"
                      min={0.5}
                      max={2}
                      step={0.05}
                      value={override.subheadFontScale}
                      onChange={(value)=> updateOverride(
                        layoutId,
                        'subheadFontScale',
                        value
                      )}
                    />
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OverrideSlider({label,min,max,step,value,onChange}) {
  return (
    <label className="layout-override-slider">
      <span>
        <strong>{label}</strong>
        <em>{Number(value).toFixed(step<1 ? 2 : 1)}</em>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event)=> onChange(Number(event.target.value))}
      />
    </label>
  );
}

function LayoutThumbnail({layout}) {
  return (
    <span className={`layout-thumbnail preview-${layout}`}>
      <i />
      <b />
      <em />
    </span>
  );
}

function ReviewStep({
  form,
  monogram,
  writingStyle,
  brandId,
  savedPreferredLayouts,
  initialLayoutVocabulary,
  onEdit
}) {
  const [vocabLoading,setVocabLoading]=useState(false);
  const [vocabError,setVocabError]=useState(null);
  const [layoutVocabulary,setLayoutVocabulary]=useState(
    initialLayoutVocabulary || ''
  );
  const layouts=form.preferredLayouts
    .map((id)=> layoutOptions.find((layout)=> layout.id===id)?.name)
    .filter(Boolean);
  const canGenerateVocabulary=Boolean(brandId);
  const hasSavedLayouts=Boolean(savedPreferredLayouts?.length);
  const businessSections=[
    {label:'Summary',value:form.businessSummary},
    {label:'Products / services',value:form.productsServices},
    {label:'Mission',value:form.mission},
    {label:'Vision',value:form.vision},
    {label:'Target audience',value:form.targetAudience},
    {label:'Strategic value',value:form.strategicValue}
  ].filter((section)=> section.value?.trim());
  const hasBusinessContext=businessSections.length>0;

  useEffect(()=> {
    setLayoutVocabulary(initialLayoutVocabulary || '');
  },[initialLayoutVocabulary]);

  const vocabularyHelperText=!canGenerateVocabulary
    ? 'Save your brand first \u2014 layout vocabulary is built from preferred layouts stored on your brand profile.'
    : !hasSavedLayouts
      ? 'Select preferred layouts first.'
      : 'Assembles structural descriptions from your saved layout preferences for Coding Creative generation.';

  const runGenerateVocabulary=async ()=> {
    if (!brandId) return;

    setVocabLoading(true);
    setVocabError(null);

    const {data:brandRow,error:fetchError}=await api.brands.getBrand(brandId);

    if (fetchError) {
      setVocabLoading(false);
      setVocabError(fetchError.message || 'Could not load brand.');
      return;
    }

    const assembled=(brandRow.preferred_layouts || [])
      .map((layoutId)=> getPostLayoutVocabulary(layoutId))
      .filter(Boolean)
      .join(' ');

    const {error:saveError}=await api.brands.updateBrand(brandId,{
      layout_vocabulary:assembled
    });

    setVocabLoading(false);

    if (saveError) {
      setVocabError(saveError.message || 'Could not save layout vocabulary.');
      return;
    }

    setLayoutVocabulary(assembled);
  };

  return (
    <div>
      <StepHeading
        eyebrow="Final step"
        title="Your brand, at a glance."
        text="Review the identity below, then save it to your workspace."
      />

      <div className="tone-assist-row review-vocabulary-row">
        <button
          type="button"
          className="secondary-button tone-assist-button"
          disabled={!canGenerateVocabulary || !hasSavedLayouts || vocabLoading}
          onClick={runGenerateVocabulary}
        >
          {vocabLoading ? 'Generating\u2026' : 'Generate layout vocabulary'}
        </button>
        <small>{vocabularyHelperText}</small>
      </div>

      {vocabError && (
        <p className="tone-assist-error" role="alert">{vocabError}</p>
      )}

      {layoutVocabulary && (
        <div className="review-layout-vocabulary">
          <p className="review-layout-vocabulary-label">Layout vocabulary</p>
          <p className="review-layout-vocabulary-text">{layoutVocabulary}</p>
        </div>
      )}

      <div className="brand-review">
        <ReviewSection title="Basics" step={0} onEdit={onEdit}>
          <div className="review-identity">
            <span style={{background:form.primary}}>
              {form.logo ? <img src={form.logo} alt="" /> : monogram}
            </span>
            <div>
              <strong>{form.name || 'Untitled brand'}</strong>
              <small>{form.displayFont} · {form.bodyFont}</small>
            </div>
            <div className="review-swatches">
              <i style={{background:form.primary}} />
              <i style={{background:form.accent}} />
            </div>
          </div>
        </ReviewSection>
        <ReviewSection title="About your business" step={1} onEdit={onEdit}>
          {hasBusinessContext ? (
            <dl className="review-business-context">
              {businessSections.map((section)=> (
                <div key={section.label}>
                  <dt>{section.label}</dt>
                  <dd>{section.value.trim()}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="review-empty">No business context added yet.</p>
          )}
        </ReviewSection>
        <ReviewSection title="Voice & tone" step={2} onEdit={onEdit}>
          <p>
            Formality {form.tone.formality}/5 · Energy {form.tone.energy}/5 ·
            Warmth {form.tone.warmth}/5
          </p>
          {form.tone.notes && <blockquote>{form.tone.notes}</blockquote>}
          {form.ctaOptions.length>0 && (
            <ul className="review-cta-list">
              {form.ctaOptions.filter((cta)=> cta.label.trim()).map((cta,index)=> {
                const typeLabel=ctaTypeOptions.find(
                  (option)=> option.value===cta.type
                )?.label || cta.type;

                return (
                  <li key={index}>
                    <strong>{cta.label}</strong>
                    <span>{typeLabel}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </ReviewSection>
        <ReviewSection title="Writing style" step={3} onEdit={onEdit}>
          <span className="review-tag">{writingStyle}</span>
        </ReviewSection>
        <ReviewSection title="Layout preferences" step={4} onEdit={onEdit}>
          <div className="review-layout-tags">
            {layouts.map((layout,index)=> (
              <span key={layout}>
                {layout}{index===0 ? ' · Default' : ''}
              </span>
            ))}
          </div>
        </ReviewSection>
      </div>
    </div>
  );
}

function ReviewSection({title,step,onEdit,children}) {
  return (
    <section className="review-section">
      <div>
        <h3>{title}</h3>
        <button type="button" onClick={()=> onEdit(step)}>Edit</button>
      </div>
      {children}
    </section>
  );
}

function ColorField({label,value,onChange}) {
  return (
    <label className="palette-field">
      <span>{label}</span>
      <span className="color-control">
        <input
          type="color"
          value={value}
          onChange={(event)=> onChange(event.target.value)}
        />
        <code>{value}</code>
      </span>
    </label>
  );
}

function FontField({label,value,options,onChange}) {
  return (
    <label className="brand-field">
      {label}
      <select value={value} onChange={(event)=> onChange(event.target.value)}>
        {options.map((font)=> (
          <option key={font} value={font}>{font}</option>
        ))}
      </select>
    </label>
  );
}

export default BrandSetup;